import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import LinearProgress from '@mui/material/LinearProgress';
import { catalogApiRef, useEntity } from '@backstage/plugin-catalog-react';
import { useApi } from '@backstage/core-plugin-api';
import {
  Entity,
  parseEntityRef,
  RELATION_HAS_PART,
} from '@backstage/catalog-model';
import type { DataTableColumn } from '@mercedes-benz/odp-theme-v2';
import { DataTable } from '@mercedes-benz/odp-theme-v2';
import { OwnerLink } from '../components/OwnerLink';

export const ComponentsTab = () => {
  const { entity } = useEntity();
  const catalogApi = useApi(catalogApiRef);
  const navigate = useNavigate();

  const [relatedEntities, setRelatedEntities] = useState<Entity[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const relations = (entity.relations || []).filter(
      r =>
        r.type === RELATION_HAS_PART &&
        parseEntityRef(r.targetRef).kind.toLowerCase() === 'component',
    );

    if (relations.length === 0) {
      setRelatedEntities([]);
      setLoading(false);
      return () => {};
    }

    const fetchRelated = async () => {
      const { items } = await catalogApi.getEntitiesByRefs({
        entityRefs: relations.map(r => r.targetRef),
      });
      if (!cancelled) {
        setRelatedEntities(items.filter((e): e is Entity => e !== undefined));
        setLoading(false);
      }
    };

    fetchRelated().catch(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [entity, catalogApi]);

  const columns: DataTableColumn[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (row: any) => (
        <span
          role="link"
          tabIndex={0}
          style={{ color: '#4A9EFF', cursor: 'pointer', fontWeight: 500 }}
          onClick={() =>
            navigate(`/catalog/${row.namespace}/${row.kind}/${row.name}`)
          }
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ')
              navigate(`/catalog/${row.namespace}/${row.kind}/${row.name}`);
          }}
        >
          {row.name}
        </span>
      ),
    },
    {
      id: 'owner',
      label: 'Owner',
      sortable: true,
      render: (row: any) => <OwnerLink owner={row.owner} />,
    },
    { id: 'type', label: 'Type', sortable: true },
    { id: 'lifecycle', label: 'Lifecycle', sortable: true },
    { id: 'description', label: 'Description', sortable: false },
  ];

  const data = relatedEntities.map(e => ({
    name: e.metadata.name,
    namespace: e.metadata.namespace || 'default',
    kind: e.kind.toLowerCase(),
    owner: (e.spec?.owner as string) || '',
    type: (e.spec?.type as string) || '',
    lifecycle: (e.spec?.lifecycle as string) || '',
    description: e.metadata.description || '',
  }));

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <DataTable
      title="Components"
      totalCount={data.length}
      columns={columns}
      data={data}
      searchable
      pagination
    />
  );
};
